import { Button, Modal } from "antd";
import { ArrowRight, ChevronLeft, Save } from "lucide-react";
import { FC } from "react";
import { useNotification } from "~/hooks";
import { useUpdateInventory } from "~/services/inventory";
import { Steps } from "../steps";
import { useConsolidateStore } from "./consolidate.store";
import { ReviewConfirm } from "./review-confirm";
import { SelectContainer } from "./select-container";
import { SelectPieces } from "./select-pieces";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const ConsolidateInventory: FC<Props> = ({ open, onClose }) => {
  const { success, error } = useNotification();

  const { step, setStep, selectedContainer, selectedPieces, resetState } =
    useConsolidateStore();

  const steps = [
    {
      key: 1,
      label: "Select Container",
      content: <SelectContainer />,
    },
    {
      key: 2,
      label: "Select Pieces",
      content: <SelectPieces />,
    },
    {
      key: 3,
      label: "Review & Confirm",
      content: <ReviewConfirm />,
    },
  ];

  const { mutate, isPending } = useUpdateInventory();

  // Handle final consolidation
  const handleConsolidate = () => {
    if (!selectedContainer || selectedPieces.length === 0) return;

    const pieceIds = selectedPieces.map((piece) => piece.id);

    mutate(
      { id: selectedContainer.id, pieceIds },
      {
        onSuccess: () => {
          success({
            message: "Consolidation Successful",
            description: "Inventory consolidated successfully.",
          });
          closeConsolidate();
        },
        onError: (err) => {
          // Handle error
          console.error(err);
          error({
            message: "Consolidation Failed",
            description: "Failed to consolidate inventory.",
          });
        },
      }
    );
  };

  // Move to next/previous step
  const goToNextStep = () => {
    setStep(step + 1);
  };

  const goToPreviousStep = () => {
    setStep(step - 1);
  };

  const closeConsolidate = () => {
    resetState();
    onClose();
  };

  return (
    <Modal open={open} onCancel={closeConsolidate} width={1000} footer={null}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-1">
            <h1 className="text-2xl font-semibold">Consolidate Inventory</h1>
          </div>
          <p className="text-gray-600">
            Group pieces into a container for easier storage and transport.
          </p>
        </div>

        {/* Progress Steps */}
        <Steps current={step} steps={steps} />

        {steps[step - 1].content}

        <div className="pt-5 flex justify-between">
          {step > 1 ? (
            <button
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              onClick={goToPreviousStep}
            >
              <ChevronLeft size={16} />
              <span>Back</span>
            </button>
          ) : (
            <div />
          )}
          {step < steps.length ? (
            <Button
              size="large"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={goToNextStep}
              disabled={
                (step === 1 && !selectedContainer) ||
                (step === 2 && selectedPieces.length === 0)
              }
            >
              <span>Continue</span>
              <ArrowRight size={16} />
            </Button>
          ) : null}
          {step === steps.length && (
            <Button
              size="large"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              onClick={handleConsolidate}
              disabled={!selectedContainer || selectedPieces.length === 0}
              loading={isPending}
            >
              <Save size={16} />
              <span>Confirm Consolidation</span>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
